import { Analytika } from "@analytika/analytika-web";
import { v4 as uuidv4 } from "uuid";
import { PhoneHasher } from "./utils/phoneHasher";
import { EmailHasher } from "./utils/emailHasher";
import i18n from "i18next";
import { ANALYTIKA_EVENTS } from "./utils/constants";

if (typeof window !== "undefined") {
  const env = process.env.REACT_APP_ANALYTIKA_ENV;
  Analytika.init(env, false, {}, 10, 10);

  if (document.visibilityState !== undefined) {
    document.addEventListener("visibilitychange", function () {
      // fires when user switches tabs, apps, goes to homescreen, etc.
      if (document.visibilityState === "hidden") {
        Analytika.getInstance().flush();
      }
    });
  }
}

class EventAnalytics {
  constructor() {
    this.invoiceId = undefined;
    this.userId = undefined;
    this.session_id = uuidv4();
    this.phoneNumber = undefined;
    this.hashedPhoneNumber = null;
    this.hashedPhoneNumberPromise = null;
    this.email = undefined;
    this.hashedEmail = null;
    this.hashedEmailPromise = null;
    this.phoneCountry = undefined;
    this.intent = undefined;
    this.page_name = undefined;
    this.flow_name = undefined;
    this.globalAttrs = {
      session_id: this.session_id,
      appName: process.env.REACT_APP_ANALYTIKA_EVENT_SOURCE,
      userAgent: navigator.userAgent,
      careem_identity_device_id: "not_available",
      client_id: "not_defined",
    };
  }

  publishV2(event, eventData) {
    if (!this.hashedPhoneNumberPromise && !this.hashedEmailPromise) {
      // both hashes (if any) are already cached: publish synchronously so a
      // redirect immediately after this call can't drop the event.
      this.doPublish(event, eventData, this.hashedPhoneNumber, this.hashedEmail);
      return Promise.resolve();
    }
    return Promise.all([
      this.hashedPhoneNumberPromise || Promise.resolve(this.hashedPhoneNumber),
      this.hashedEmailPromise || Promise.resolve(this.hashedEmail),
    ]).then(([hashedPhoneNumber, hashedEmail]) => {
      this.doPublish(event, eventData, hashedPhoneNumber, hashedEmail);
    });
  }

  doPublish(event, eventData, hashedPhoneNumber, hashedEmail) {
    Analytika.publish({
      event_name: event.event_name,
      event_version: event.event_version ?? 1,
      signature: event.signature,
      event_trigger_time: Date.now(),
      invoice_id: this.invoiceId,
      user_id: this.userId,
      hashed_phone_number: hashedPhoneNumber,
      hashed_user_email: hashedEmail,
      intent: this.intent,
      flow_name: this.flow_name,
      page_name: this.getPageName(),
      language: i18n.language,
      session_id: this.session_id,
      ...(this.phoneCountry && { phone_country: this.phoneCountry }),
      ...this.globalAttrs,
      ...eventData,
    });
  }
  flush() {
    return Analytika.getInstance().flush();
  }

  publishViewPage(page_name, extraAttrs) {
    return this.publishV2(ANALYTIKA_EVENTS.IDNW_VIEW_PAGE, {
      page_name,
      ...extraAttrs,
    });
  }
  setInvoiceId(invoiceId) {
    this.invoiceId = invoiceId;
  }

  setUserId(userId) {
    this.userId = userId;
  }

  setIntent(intent) {
    this.intent = intent;
  }

  setFlowName(flow_name) {
    this.flow_name = flow_name;
  }

  setPageName(page_name) {
    this.page_name = page_name || "not_available";
  }

  setPhoneNumber(phoneNumber) {
    this.phoneNumber = phoneNumber;
    if (!phoneNumber) {
      this.hashedPhoneNumber = null;
      this.hashedPhoneNumberPromise = null;
      return;
    }
    this.hashedPhoneNumber = null;
    this.hashedPhoneNumberPromise = PhoneHasher.sha256Hex(phoneNumber).then(
      (hash) => {
        this.hashedPhoneNumber = hash;
        this.hashedPhoneNumberPromise = null;
        return hash;
      }
    );
  }

  setEmail(email) {
    this.email = email;
    if (!email) {
      this.hashedEmail = null;
      this.hashedEmailPromise = null;
      return;
    }
    this.hashedEmail = null;
    this.hashedEmailPromise = EmailHasher.sha256Hex(email).then((hash) => {
      this.hashedEmail = hash;
      this.hashedEmailPromise = null;
      return hash;
    });
  }

  setDeviceId(device_id) {
    this.globalAttrs.careem_identity_device_id = device_id;
  }

  setClientId(clientId) {
    this.globalAttrs.client_id = clientId || "not_defined";
  }

  getPageName() {
    return this.page_name || "not_available";
  }

  getSessionUuid() {
    return this.session_id;
  }

  setPhoneCountry(phoneCountry) {
    this.phoneCountry = phoneCountry;
  }
}

const eventAnalytics = new EventAnalytics();
export default eventAnalytics;
